import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';

import { EmailStatus } from 'utils/enums';

import { UserState } from './user.types';

export const initialState: UserState = {
  _id: '',
  aboutMe: '',
  accessToken: '',
  address: '',
  authSessionKey: '',
  authSource: '',
  availableReview: false,
  brazeExternalId: '',
  changePassword: null,
  city: '',
  companyPartners: { connectedPartners: [], onboardingPartner: null },
  customerId: '',
  dob: '',
  doctorId: '',
  dynamicLink: '',
  elationId: '',
  email: '',
  emailStatus: EmailStatus.Legacy,
  features: {
    addToCalendarOnAppointmentCards: { status: 'disabled' },
    aiAssistantChat: { status: 'disabled' },
    annual45: { status: 'disabled' },
    choosingDoctor: { status: 'disabled' },
    clickToCall: { status: 'disabled' },
    complimentaryMedications: { status: 'disabled' },
    documentsAndLinks: { status: 'enabled' },
    frontDesk: { status: 'disabled' },
    healthProfile: { status: 'enabled' },
    labsAndImaging: { status: 'enabled' },
    medicalRecords: { status: 'enabled' },
    messaging: { status: 'disabled' },
    migrateToBundlePlan: { status: 'enabled' },
    myProvider: { status: 'enabled' },
    noShowCharge: { status: 'disabled' },
    notifications: { status: 'disabled' },
    offerAsyncAppointments: { status: 'enabled' },
    prescriptionDiscountCard: { status: 'disabled' },
    prescriptions: { status: 'disabled' },
    reOnboardingWM: { status: 'disabled' },
    scheduleAppointment: { status: 'disabled' },
    store: { status: 'disabled' },
    writeMessage: { status: 'disabled' }
  },
  firstName: '',
  gender: '',
  globalSettings: [],
  hasActiveOrders: 0,
  hasActiveResults: 0,
  hasInsurance: false,
  hasPassword: true,
  hasPasswordResetToken: false,
  hasSubscription: false,
  identityVerified: false,
  identityVerifiedAt: null,
  initialLoginVideo: '',
  isEmailVerified: false,
  isInitialLogin: false,
  isVerifiedByVouched: null,
  isVerifyingPending: false,
  lastLogin: '',
  lastLoginClientPlatform: '',
  lastName: '',
  lastSentRating: 0,
  nextSubscriptionRenewalAt: null,
  nickName: '',
  onboardingTask: { _id: '', category: '', status: '' },
  patientPreferences: null,
  paymentProfiles: [],
  pharmacy: {
    _id: '',
    address: '',
    city: '',
    fax: '',
    latitude: 0,
    longitude: 0,
    name: '',
    pharmacyId: 0,
    phone: '',
    state: '',
    zipCode: ''
  },
  phone: {
    countryCode: '',
    countryPrefix: '',
    internationalFormat: '',
    phoneNumber: ''
  },
  profileImage: '',
  refreshToken: '',
  registrationDate: '',
  reviewedProviders: [],
  sexAtBirth: '',
  state: '',
  status: '',
  subscriptionId: '',
  timezone: '',
  userId: '',
  verifyEmail: null,
  zipCode: ''
};

const userSlice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    clearUser: (): UserState => initialState,
    /**
     * A session held by the login service (`@thecvlb/auth-web`). The refresh credential is a cookie
     * on the login host and never reaches the page, so no refresh token is stored.
     */
    setLoginServiceSession: (
      state,
      {
        payload: { accessToken, sessionKey, source }
      }: PayloadAction<{ accessToken: string; sessionKey: string; source: string }>
    ) => {
      state.accessToken = accessToken;
      state.refreshToken = '';
      state.authSessionKey = sessionKey;
      state.authSource = source;
    },
    setToken: (
      state,
      {
        payload: { accessToken, refreshToken }
      }: PayloadAction<{ accessToken: string; refreshToken: string }>
    ) => {
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    },
    setUser: (state, action: PayloadAction<Partial<UserState>>) => {
      if (action.payload._id) {
        action.payload = { ...action.payload, userId: action.payload._id };
      }

      return {
        ...state,
        ...action.payload,
        // to remove unpaid invoice notification when user pays the invoice
        semaglutideMigration: action.payload.semaglutideMigration
      };
    }
  }
});

export const { clearUser, setLoginServiceSession, setToken, setUser } = userSlice.actions;

export const selectUser = (state: RootState) => state.user;
/**
 * Returns the user's Bridge patient ID, or `undefined` when the user is
 * not routed to the Bridge billing path (or the backend worker hasn't
 * created the Bridge record yet). Populated by LMD-25651. Consumed by
 * post-onboarding Bridge-aware flows (insurance update, re-consent).
 *
 * Input type is narrowed to `Pick<RootState, 'user'>` so tests can pass
 * just the slice the selector reads — callers that already hold a full
 * `RootState` can still pass it through via structural subtyping.
 */
export const selectBridgePatientId = (state: Pick<RootState, 'user'>): string | undefined =>
  state.user.bridgePatientId;
export const selectWriteMessageFeatureStatus = (state: RootState) =>
  state.user.features?.writeMessage;

export default userSlice.reducer;
